/**
 * @flow
 */

import Config from 'config';
import qs from 'querystring';
import 'core-js/fn/regexp/escape';

import type { WidgetParams, WidgetInitState } from './widget';

/**
 * Build a custom event with data.
 */
export function buildEvent(type: string, data: any): CustomEvent {
  const event: CustomEvent = document.createEvent('CustomEvent');
  event.initCustomEvent(`unii:${type}`, true, false, data);
  return event;
}

/**
 * Extracts a valid event id from a url.
 */
export function extractId(url: string): string {
  // $FlowFixMe Non-standard RegExp method
  const regex = new RegExp(`(?:${RegExp.escape(Config.api.host)}|clkn\/.*)\/events\/([^/?#]+)`);
  const match = url.match(regex);

  if (match) {
    return match[1];
  }

  return '';
}

export function extractParam(url: string, param: string): string {
  const params: string = url.split('?')[1];
  if (params) {
    return qs.parse(params)[param] || '';
  }
  return '';
}

/**
 * Retreive cookie.
 */
export function getCookie(key: string): ?string {
  // $FlowFixMe Non-standard RegExp method
  const regex = new RegExp(`(?:^|;)(?:\\s*)${RegExp.escape(key)}=([^;]+)`);
  const match = document.cookie.match(regex);

  if (match && match[1]) {
    return decodeURIComponent(match[1]);
  }

  return null;
}


/**
 * Retrieve user-set cookie to disable analytic tracking.
 */
export function doNotSell(): boolean {
  const cookie = getCookie('do_not_sell');
  if (cookie) {
    return JSON.parse(cookie);
  }
  return false;
}

/**
 * Retrieve user-set locale
 */
export function cookieLocale(): ?string {
  const cookie = getCookie('locale');
  if (cookie) {
    return cookie;
  }
  return null;
}


/**
 * Retrieve exact ClientId.
 */

export function getClientId(key: string): ?string {
  // $FlowFixMe Non-standard RegExp method
  const regex = new RegExp(`(?:^|;)(?:\\s*)${RegExp.escape(key)}=([^;]+)`);
  const match = document.cookie.match(regex);
  // GA sets the cookie so we don't need to decodeURI
  if (match && match[0]) {
    const firstMatch: ?string[] = match[0].match(/\d+?\.\d+$/);
    if (firstMatch && firstMatch[0]) {
      return firstMatch[0];
    }
  }

  return null;
}

/**
 * Set cookie.
 */
export function setCookie(key: string, value: string): void {
  document.cookie = `${key}=${encodeURIComponent(value)}; expires=Fri, 31 Dec 2037 23:59:59 GMT; path=/;`;
}

const clean = (obj: { state: string }): WidgetParams => {
  const cleaned = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== null && obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });

  if (!cleaned['state']) {
    cleaned['state'] = '';
  }

  return cleaned;
};

const parseParam = (rawParams: string, name: string): ?string => {
  return [qs.parse(rawParams.slice(1))[name] || null].flat().slice(-1)[0];
};

export function getParam(name: string): ?string {
  return parseParam(window.location.search, name);
}

export const getParams = (rawParams: string, initState: WidgetInitState): WidgetParams => {
  const getParam = parseParam.bind(null, rawParams);

  const accessKey: ?string = getParam('unii-access-key');
  const code: ?string = getParam('unii-discount-code');

  const groupSize: ?string = getParam('groupSize');
  const ticketType: ?string = getParam('ticketType');
  const timeOfDay: ?string = getParam('timeOfDay');

  // IMPT: Having the same clientId, cuts transactions down to 1 instead of 2.
  // getCookie preserves the session, extractClientId just starts new sessions.
  // Copies the clientId exactly without Ga.1.1
  const clientId: ?string = getClientId('_ga');
  const hostRef: ?string = document.referrer || null;
  const hostUrl: ?string = window.location.href || null;
  let ref: ?string = getParam('universe-ref') || getParam('ref') || getCookie('uniiverse_ref');

  // if we have this universe_facebook_push set in the cookie but not in the URL,
  // do not use this as the ref value. It has to be in the 'ref' param.
  if (getCookie('uniiverse_ref') === 'universe_facebook_push' && getParam('ref') !== 'universe_facebook_push') {
    ref = getParam('universe-ref') || getParam('ref');
  }

  // don't save the universe_facebook_push ref as a cookie
  if (ref && ref !== 'universe_facebook_push') {
    setCookie('uniiverse_ref', ref);
  }

  const defaultState = {
    analytics: {
      host: {
        ref: hostRef,
        url: hostUrl
      },
      google: { clientId }, ref
    },
    accessKeys: [],
    currentDiscountCode: { code },
    groupSize: parseFloat(groupSize) || null,
    ticketType,
    timeOfDay,
  };

  const finalState = { ...defaultState, ...initState };

  if (accessKey) {
    finalState.accessKeys.push(accessKey);
  }

  return clean({
    state: JSON.stringify(finalState),
    utm_campaign: getParam('utm_campaign'),
    utm_medium: getParam('utm_medium'),
    utm_source: getParam('utm_source'),
    utm_term: getParam('utm_term'),
    utm_name: getParam('utm_name')
  });
};

export const isMobile = (userAgent: string): boolean =>
  /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|MSIE 9.0/i.test(navigator.userAgent);

// get current running script's instance to extract widget info
export function getCurrentScript(): ?HTMLElement {
  if (document.currentScript) { return document.currentScript; }

  const scripts: NodeList<HTMLElement> = document.querySelectorAll(
    'script[src$="embed2.js"]'
  );
  return scripts[scripts.length - 1];
}



// WEBPACK FOOTER //
// ./src/bootstrap/util.js